import { useNavigate } from 'react-router-dom';
import { parseItemsToIds } from 'utils/parseItemsToIds';

import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import ApplicationFormFields from 'components/forms/ApplicationFormFields';

import useApplication from 'hooks/useApplication';
import useField from 'hooks/useField';
import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';
import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import { APPLICATION_CREATE_REQUEST_STATUS, TEST_ID } from './constants';
import type { CreateApplicationFormSchema, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const CreateApplicationForm: FunctionComponent<Props> = ({
    className = '',
    onCancelClose,
    onClose,
    onCreated,
    requestingClose,
    testId = TEST_ID
}) => {
    const { createApplication, isLoading } = useApplication();
    const { CREATE, NEXT_PLAN_THE_FLIGHT } = useTranslation();
    const { applicationDetailsPage } = usePageRoutes();
    const navigate = useNavigate();
    const { assignFieldToBoundary } = useField();
    const form = useForm<CreateApplicationFormSchema>({
        mode: 'onChange'
    });

    const {
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose,
        onClose,
        requestingClose
    });

    const onSubmit = async (data: CreateApplicationFormSchema) => {
        const { batteries, boundaryId, drones, field, name, pilotIds } = data;

        if (field?.value && boundaryId?.value) {
            await assignFieldToBoundary({
                boundaryId: boundaryId?.value,
                siteId: field?.value
            });
        }

        const application = await createApplication({
            application: {
                batteryIds: parseItemsToIds(batteries),
                boundaryId: boundaryId?.value ?? null,
                droneIds: parseItemsToIds(drones),
                name,
                scheduledPilotIds: parseItemsToIds(pilotIds),
                siteId: field?.value,
                status: APPLICATION_CREATE_REQUEST_STATUS.NOT_STARTED
            }
        });

        if (application) {
            navigate(applicationDetailsPage(application.id), { state: { isEditingMap: true } });
            onCreated?.();
        }
    };

    return (
        <VerticalContainer
            className={`${styles.createApplicationForm} ${className}`}
            testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form form={form}>
                    <ApplicationFormFields />

                    <FormSubmitPanel
                        buttonText={CREATE}
                        disabled={isLoading}
                        errors={errors}
                        nextText={NEXT_PLAN_THE_FLIGHT}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default CreateApplicationForm;
